import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import Reveal from './Reveal';

const isVideoFile = (url) => !!url && ['.mp4', '.webm', '.ogg', '.mov'].some((ext) => url.toLowerCase().endsWith(ext));

const EventMeta = ({ date, day, month, eventName, location }) => (
  <div className="flex items-center">
    <div className="border-r border-gray-300 dark:border-white/15 mr-4 w-[110px] md:w-[150px] flex-shrink-0 p-1">
      {date ? (
        <div className="flex items-center">
          <div className="flex-none text-center pr-1">
            <div className="text-[35px] md:text-[64px] font-glsnecb leading-none text-gray-900 dark:text-white">{date}</div>
          </div>
          <div className="flex flex-col justify-between pl-4">
            <div className="font-myraid text-sm md:text-[17px] text-gray-900 dark:text-white">{day}</div>
            <div className="font-myraid text-sm md:text-[17px] text-gray-900 dark:text-white">{month}</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-2">
          <span className="font-glsnecb text-[18px] md:text-[22px] leading-none text-gray-700 dark:text-gray-300">NOVEMBER</span>
          <span className="font-myraid text-[9px] md:text-[10px] uppercase tracking-[0.14em] text-gray-400 mt-1">Date to be announced</span>
        </div>
      )}
    </div>
    <div>
      <div className="font-myraid text-xl md:text-[32px] leading-tight text-gray-900 dark:text-white">{eventName}</div>
      <div className="md:text-md text-sm text-gray-500 dark:text-gray-400">
        {location || <span className="italic text-gray-400">Location: To be announced</span>}
      </div>
    </div>
  </div>
);

const EventCard = ({ date, day, month, eventName, location, image }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  return image ? (
    // Poster card — shown only when the event has an image, sized like an Instagram post.
    // Full poster always shown, never cropped — the image keeps its natural aspect ratio.
    <div className="w-full max-w-md mx-auto bg-white dark:bg-[#111010] border border-gray-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-md dark:shadow-black/40 mb-8">
      <div className="w-full bg-black flex items-center justify-center overflow-hidden">
        <img
          src={image}
          alt={eventName}
          className={`w-full h-auto max-h-[75vh] object-contain transition-opacity duration-700 ease-out ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
        />
      </div>
      <div className="flex flex-col gap-4 p-5">
        <EventMeta date={date} day={day} month={month} eventName={eventName} location={location} />
        <Link to="/donation" className="bg-black dark:bg-white dark:text-black text-white px-4 py-2 rounded text-center whitespace-nowrap transition-colors hover:opacity-90">
          Support
        </Link>
      </div>
    </div>
  ) : (
    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 p-4 border-b border-gray-300 dark:border-white/10">
      <EventMeta date={date} day={day} month={month} eventName={eventName} location={location} />
      <Link to="/donation" className="bg-black dark:bg-white dark:text-black text-white px-4 py-2 rounded self-start sm:self-auto whitespace-nowrap transition-colors hover:opacity-90">
        Support
      </Link>
    </div>
  );
};

// Three placeholder rows shown only while the first fetch is in flight, so
// the page never flashes "No upcoming events" before real data has loaded.
const EventsSkeleton = () => (
  <div aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-200 dark:border-white/10 animate-pulse">
        <div className="w-[110px] md:w-[150px] h-16 bg-gray-200 dark:bg-white/10 rounded flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-1/3 bg-gray-200 dark:bg-white/10 rounded" />
          <div className="h-3 w-1/4 bg-gray-200 dark:bg-white/10 rounded" />
        </div>
        <div className="w-24 h-9 bg-gray-200 dark:bg-white/10 rounded" />
      </div>
    ))}
  </div>
);

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/post/all`);
        const data = await response.json();
        const filteredEvents = data.filter((event) => event.category === 'Event');
        setEvents(filteredEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <div className="bg-white dark:bg-black min-h-screen transition-colors duration-300">
      <div className="md:w-[80%] mx-auto pt-[150px] pb-16">
        <Reveal as="h1" className="text-center text-[60px] md:text-[94px] font-glsnecb mb-8 text-gray-900 dark:text-white">
          UPCOMING EVENTS
        </Reveal>

        {loading ? (
          <EventsSkeleton />
        ) : events.length > 0 ? (
          events.map((event, i) => (
            <Reveal key={event.ID} delay={Math.min(i, 4) * 80}>
              <EventCard
                date={event.date ? Number(new Date(event.date).toLocaleDateString('en-US', { day: 'numeric', timeZone: 'Africa/Kigali' })) : null}
                day={event.date ? new Date(event.date).toLocaleString('en-us', { weekday: 'long', timeZone: 'Africa/Kigali' }) : null}
                month={event.date ? new Date(event.date).toLocaleString('en-us', { month: 'long', timeZone: 'Africa/Kigali' }) : null}
                eventName={event.title}
                location={event.location}
                image={event.image_video && !isVideoFile(event.image_video) ? `${process.env.REACT_APP_BASE_URL}${event.image_video}` : null}
              />
            </Reveal>
          ))
        ) : (
          <p className="text-center text-gray-500 dark:text-gray-400">No upcoming events available.</p>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Events;
